import React, { memo, useMemo } from "react";
import { Container, useApp } from "@pixi/react";
import FreeSpinReel from "../FreeSpinReel/FreeSpinReel";
import { useReelRefs } from "../../../Contexts/ReelRefsContext";
import SpineTextureExtractor from "../../../utils/SpineTextureExtractor";
import { useSelector } from "react-redux";
import { selectCurrentRound, selectFreeSpinActive } from "../../../State/selectors";

// Module-level cache for free spin reels (same pattern as main ReelSet)
let cachedFreeSpinReels = null;

/**
 * FreeSpinReelSet
 * Renders 5 reels with instant symbol display — no spin animation.
 * Same positioning, sizing, mask, and z-index as the main ReelSet.
 * Reuses the main ReelSet's mask via reelSetMaskRef (no duplicate mask).
 */
const FreeSpinReelSet = memo(
  ({ symbolWidth, symbolHeight, dividerDimensions, reelSpacing }) => {
    const { freeSpinReelRefs, reelSetMaskRef } = useReelRefs();
    const currentRound = useSelector(selectCurrentRound);
    const freeSpinActive = useSelector(selectFreeSpinActive);

    // Cache reels at module level — same pattern as main game
    if (!cachedFreeSpinReels && currentRound?.reels) {
      cachedFreeSpinReels = currentRound.reels;
    }

    const reels = cachedFreeSpinReels;

    const app = useApp();

    // Extract spine static frames as textures (no blur sheet needed for free spins)
    const symbolsSpriteSheet = useMemo(() => {
      if (app?.renderer) {
        try {
          const extractor = new SpineTextureExtractor(app.renderer);
          const extracted = extractor.extractAll();
          if (extracted?.textures && Object.keys(extracted.textures).length > 0) {
            return extracted;
          }
        } catch {
          // Fall through to null
        }
      }
      return null;
    }, [app?.renderer]);

    // Reel container Y position
    // Free spin uses 5 sprites (1 buffer + 3 visible + 1 buffer)
    // Sprites at indices 0..4, visible at 1,2,3
    // Sprite centers at i*symbolHeight relative to container
    // Visible sprite 1 center should be at -symbolHeight (top of visible area)
    // containerY + 1*symbolHeight = -symbolHeight → containerY = -2*symbolHeight
    const reelContainerY = useMemo(
      () => -2 * symbolHeight,
      [symbolHeight],
    );

    // Reel X positions (same formula as main game)
    const reelPositions = useMemo(() => {
      return reels
        ? reels.map((_, index) => ({
            x: reelSpacing * (index - 2),
            y: reelContainerY,
          }))
        : [];
    }, [reels, reelSpacing, reelContainerY]);

    // Guard: Don't render until reels and sprite textures are ready
    if (
      !reels ||
      !Array.isArray(reels) ||
      reels.length === 0 ||
      !symbolsSpriteSheet?.textures
    ) {
      return null;
    }

    return (
      <Container
        name="free_spin_reelset"
        mask={reelSetMaskRef?.current || null}
        x={0}
        y={0}
        zIndex={1}
        visible={freeSpinActive}
      >
        {reels.map((reel, index) => {
          const position = reelPositions[index];
          if (!position) return null;

          return (
            <FreeSpinReel
              key={index}
              ref={(el) => (freeSpinReelRefs.current[index] = el)}
              reel={reel}
              index={index}
              x={position.x}
              y={position.y}
              symbolWidth={symbolWidth}
              symbolHeight={symbolHeight}
              symbolsSpriteSheet={symbolsSpriteSheet}
            />
          );
        })}
      </Container>
    );
  },
);

export default FreeSpinReelSet;
